import { mande, type MandeError } from 'mande'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/store/user'
import { baseDec } from '@/composables/utilities/general'

function appendValue(fd: FormData, key: string, value: any) {
  if (value === undefined || value === null) {
    fd.append(key, '')
    return
  }
  if (value instanceof File || value instanceof Blob) {
    fd.append(key, value, (value as File).name || 'file')
    return
  }
  if (value instanceof Date) {
    fd.append(key, value.toISOString())
    return
  }
  if (Array.isArray(value)) {
    if (value.length && (value[0] instanceof File || value[0] instanceof Blob)) {
      value.forEach((file: File) => fd.append(key, file, file.name))
      return
    }
    value.forEach((v) => fd.append(`${key}[]`, String(v)))
    return
  }
  if (typeof value === 'object') {
    fd.append(key, JSON.stringify(value))
    return
  }
  fd.append(key, String(value))
}

function toFormData(payload: Record<string, any>): FormData {
  const fd = new FormData()
  Object.entries(payload).forEach(([k, v]) => appendValue(fd, k, v))
  return fd
}

export function useFormSubmit() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/form`)
  const language = useI18n().locale.value.split('-')[0]
  const response = ref<any>(null)
  const submitting = ref(false)
  const error = ref<MandeError | null>(null)
  const { isLoggedIn } = useUserStore()

  const submitForm = async (
    formId: string,
    formDataPayload: Record<string, any>,
    platform: string = 'web',
  ) => {
    submitting.value = true
    error.value = null
    try {
      const fd = toFormData(formDataPayload)
      const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : null
      const userSessionId =
        typeof window !== 'undefined' ? localStorage.getItem('user-session-id') : null

      const endpoint = isLoggedIn ? `private/${formId}/submit` : `${formId}/submit`

      const headers: Record<string, string> = {
        'Accept-Language': language,
        'X-Client-Platform': platform,
      }
      if (isLoggedIn && authToken) headers.Authorization = authToken
      if (userSessionId) {
        headers['pragma-cache-local'] = baseDec(userSessionId)
      }

      const apiResponse = await api.post<{ payload: any }>(endpoint, fd, { headers })
      response.value = apiResponse.payload
      return response.value?.status === 'ok'
    } catch (err) {
      error.value = err as MandeError
      return false
    } finally {
      submitting.value = false
    }
  }

  return {
    submitForm,
    submitting,
    error,
    response,
  }
}
